import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { useSEO } from "@/lib/useSEO";
import { api } from "@/lib/api";
import { faqs } from "@/lib/faqs";
import FAQAccordion from "@/components/FAQAccordion";
import { ArrowRight, Video, Clock, ShieldCheck, CheckCircle2, Trophy, Users, Zap, Target } from "lucide-react";
import { PHOTO_RING_PAD_WORK } from "@/lib/brand";

const FEATURE_ICONS = [Video, Target, Trophy, Zap];
const FEATURES_FALLBACK = [
  { title: "Live 1-on-1 Zoom Sessions", body: "Real-time technique correction over Zoom — not pre-recorded videos. Coach Chris sees what you're doing and adjusts in the moment, just like in-person." },
  { title: "Boxing Footwork Training Online", body: "Stance, pivots, angles, lateral movement — taught with deliberate drills designed to translate from your living room to the ring. Footwork is the foundation of everything." },
  { title: "Boxing Coach for Beginners (and beyond)", body: "Brand new to boxing? You're welcome here. Coach Chris specializes in making the basics feel accessible without watering anything down. Intermediate and competitor levels also available." },
  { title: "Boxing Fitness Coaching Online", body: "Build genuine fight-shape conditioning — combinations, defensive movement, slip-and-counter work. The fitness comes from the technique, not generic cardio." },
];
const TRUST_ICONS = [Users, Clock, ShieldCheck];

export default function Services() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/page-config/services").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  const c = cfg || {};
  const hero = c.hero || {}, feat = c.features || {}, how = c.how || {}, trust = c.trust || {}, faqSec = c.faq_section || {}, finalCta = c.final_cta || {};
  const features = feat.cards || FEATURES_FALLBACK;
  const steps = how.steps || [
    { n: "01", title: "Pay & reserve", body: "Lock in your package securely. No date-picking at checkout — coach contacts you within 24 hours." },
    { n: "02", title: "Coach calls to schedule", body: "Coach Chris reaches out by phone, text, or email to coordinate a time that works for both of you." },
    { n: "03", title: "Train on Zoom", body: "Live, private, fully-coached — get on Zoom, get corrected in real time, get better." },
  ];
  const trustCards = trust.cards || [
    { title: "100+ Athletes Coached", sub: "Beginners through competitors" },
    { title: "7+ Years Coaching", sub: "Coach Chris's full coaching career" },
    { title: "Reschedule-Friendly", sub: "24h notice = free reschedule" },
  ];

  useSEO({
    title: "Online Boxing Coach — Zoom Boxing Coaching & Virtual Lessons | Better Through Boxing",
    description: "Train 1-on-1 with an online boxing coach over Zoom. Live virtual boxing lessons covering footwork, defense, the Core 4 punches, and fitness conditioning. The BTB Basics Program (6 weeks, $699) or single sessions ($100, $70 alumni).",
    path: "/services",
    keywords: ["online boxing coach", "zoom boxing coaching", "virtual boxing lessons", "boxing coach for beginners", "boxing fitness coaching online", "boxing footwork training online", "private boxing coach", "live boxing classes"],
  });

  return (
    <div className="App relative">
      <SiteHeader />
      <main data-testid="services-page">
        {hero.enabled !== false && (
        <section className="theme-on-dark relative border-b border-[#27272A] overflow-hidden">
          <img src={PHOTO_RING_PAD_WORK} alt="Coach Chris running live pad work in the ring" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/80 to-[#050505]" />
          <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-24 md:py-32">
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-4">{hero.kicker || "Services · Online Boxing Coach"}</div>
            <h1 className="font-display font-bold uppercase text-5xl md:text-7xl tracking-tight leading-[0.9] mb-6">
              {hero.title_line1 || "Zoom boxing coaching"}<br /><span className="text-[#FF331F]">{hero.title_accent || "that actually corrects you."}</span>
            </h1>
            <p className="text-zinc-300 leading-relaxed max-w-2xl text-base md:text-lg mb-8 whitespace-pre-line">
              {hero.body || "Better Through Boxing offers live virtual boxing lessons with a real coach watching, correcting, and pushing you in real time over Zoom. Whether you're a complete beginner looking for the right foundation or a returning athlete tuning up your technique, this is private boxing instruction built around your goals and your schedule."}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/book?package=btb_basics_program"><Button className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm h-12 px-6" data-testid="services-cta-program">Start the Basics Program <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              <Link to="/book?package=virtual_40"><Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-900 hover:text-white font-bold uppercase tracking-widest rounded-sm bg-transparent h-12 px-6" data-testid="services-cta-single">Book a Single Session</Button></Link>
            </div>
          </div>
        </section>
        )}

        {feat.enabled !== false && (
        <section className="max-w-6xl mx-auto px-6 md:px-10 py-20">
          <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-none mb-3">{feat.heading || "What virtual boxing lessons cover."}</h2>
          <p className="text-zinc-400 max-w-2xl mb-12">{feat.intro || "Every Zoom session is structured around the fundamentals that make a real boxer — not a fitness class."}</p>
          <div className="grid md:grid-cols-2 gap-6">
            {features.map((f, i) => {
              const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
              return (
                <div key={i} className="border border-[#27272A] bg-[#0F0F0F] p-7 hover:border-[#FF331F]/50 transition-colors" data-testid={`service-card-${i}`}>
                  <Icon className="h-6 w-6 text-[#FF331F] mb-4" />
                  <h3 className="font-display uppercase text-xl mb-2 leading-tight">{f.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </section>
        )}

        {/* Packages (pricing is managed in Admin → Pricing) */}
        <section className="bg-[#0F0F0F] border-y border-[#27272A]">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-20">
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">Packages & pricing</div>
            <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-none mb-12">Two ways in.</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border-2 border-[#FF331F] bg-gradient-to-br from-[#FF331F]/10 to-transparent p-8" data-testid="package-program-card">
                <div className="text-[10px] uppercase tracking-widest text-[#FF331F] font-bold mb-2">Flagship</div>
                <h3 className="font-display uppercase text-3xl mb-2 leading-tight">BTB Basics Program</h3>
                <div className="text-4xl font-display mb-4">$699<span className="text-base text-zinc-500 font-normal"> · 6 weeks</span></div>
                <p className="text-sm text-zinc-300 mb-5 leading-relaxed">Six weeks of live private Zoom coaching covering stance, footwork, defense, and the Core 4 punches. Completing the program unlocks the <strong className="text-[#FF331F]">$70 alumni rate</strong> on all future virtual sessions.</p>
                <ul className="space-y-2 text-sm text-zinc-400 mb-6">
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#FF331F] flex-shrink-0 mt-0.5" /> 6 weekly 40-minute Zoom sessions</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#FF331F] flex-shrink-0 mt-0.5" /> Foundation in stance, footwork, head movement</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#FF331F] flex-shrink-0 mt-0.5" /> Core 4 punches with proper mechanics</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#FF331F] flex-shrink-0 mt-0.5" /> Unlocks alumni pricing forever</li>
                </ul>
                <Link to="/book?package=btb_basics_program"><Button className="w-full bg-[#FF331F] hover:bg-[#FF5242] font-bold uppercase tracking-widest rounded-sm" data-testid="services-program-book">Start the program <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              </div>
              <div className="border border-[#27272A] bg-[#050505] p-8" data-testid="package-single-card">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">Single Session</div>
                <h3 className="font-display uppercase text-3xl mb-2 leading-tight">Live Virtual Session</h3>
                <div className="text-4xl font-display mb-1">$100<span className="text-base text-zinc-500 font-normal"> / session</span></div>
                <div className="text-xs text-emerald-400 uppercase tracking-widest font-bold mb-4">Alumni: $70</div>
                <p className="text-sm text-zinc-300 mb-5 leading-relaxed">One-off 40-minute live Zoom session — perfect for technique tune-ups, conditioning days, or testing the waters before committing to the program.</p>
                <ul className="space-y-2 text-sm text-zinc-400 mb-6">
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#FF331F] flex-shrink-0 mt-0.5" /> 40-minute live private Zoom</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#FF331F] flex-shrink-0 mt-0.5" /> Footwork, defense, or pad-style drills</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#FF331F] flex-shrink-0 mt-0.5" /> Coach Chris contacts you to schedule</li>
                </ul>
                <Link to="/book?package=virtual_40"><Button variant="outline" className="w-full border-zinc-700 text-white hover:bg-zinc-900 hover:text-white font-bold uppercase tracking-widest rounded-sm bg-transparent" data-testid="services-single-book">Book a session</Button></Link>
              </div>
            </div>
          </div>
        </section>

        {how.enabled !== false && (
        <section className="max-w-6xl mx-auto px-6 md:px-10 py-20">
          <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-none mb-12">{how.heading || "How online boxing coaching works."}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="border-l-2 border-[#FF331F] pl-6">
                <div className="font-display text-5xl text-[#FF331F] leading-none mb-3">{s.n}</div>
                <h3 className="font-display uppercase text-xl mb-2">{s.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </section>
        )}

        {trust.enabled !== false && (
        <section className="bg-[#FF331F] py-12">
          <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-6 text-white">
            {trustCards.map((t, i) => {
              const Icon = TRUST_ICONS[i % TRUST_ICONS.length];
              return (
                <div key={i} className="flex items-start gap-3"><Icon className="h-6 w-6 flex-shrink-0" /><div><div className="font-display uppercase">{t.title}</div><div className="text-sm text-white/80">{t.sub}</div></div></div>
              );
            })}
          </div>
        </section>
        )}

        {faqSec.enabled !== false && (
        <section className="bg-[#0F0F0F] border-y border-[#27272A]">
          <div className="max-w-4xl mx-auto px-6 md:px-10 py-20">
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">{faqSec.kicker || "Common Questions"}</div>
            <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-none mb-3">{faqSec.heading || "Before you book."}</h2>
            <p className="text-zinc-400 mb-10 max-w-xl">{faqSec.intro || "The most-asked questions from athletes considering online boxing coaching."}</p>
            <FAQAccordion items={faqs.slice(0, 7)} schemaId="services-faq-jsonld" />
            <div className="text-center mt-8">
              <Link to="/faq" className="inline-flex items-center text-[#FF331F] text-xs uppercase tracking-widest font-bold hover:underline" data-testid="services-see-all-faq">
                See all FAQs <ArrowRight className="ml-1 h-3 w-3" />
              </Link>
            </div>
          </div>
        </section>
        )}

        {finalCta.enabled !== false && (
        <section className="max-w-4xl mx-auto px-6 md:px-10 py-24 text-center">
          <h2 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.9] mb-6">{finalCta.heading || "Ready to box, properly?"}</h2>
          <p className="text-zinc-300 max-w-2xl mx-auto mb-8">{finalCta.body || "Start with the Basics Program or grab a single session."} Want to read what athletes say first? <Link to="/testimonials" className="text-[#FF331F] hover:underline">See testimonials →</Link></p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/book"><Button className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm h-12 px-6" data-testid="services-final-cta">Book now <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            <Link to="/about"><Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-900 hover:text-white font-bold uppercase tracking-widest rounded-sm bg-transparent h-12 px-6">Meet Coach Chris</Button></Link>
          </div>
        </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
